import React, { useState, useEffect } from 'react';
import axios from '../services/axiosConfig';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import '../styles/reportes.css';

const Reportes = () => {
  const [ingresosMensuales, setIngresosMensuales] = useState([]);
  const [turnosPorEstado, setTurnosPorEstado] = useState([]);
  const [pacientesNuevos, setPacientesNuevos] = useState([]);
  const [turnosPorMes, setTurnosPorMes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodoMeses, setPeriodoMeses] = useState(6);

  useEffect(() => {
    cargarReportes();
  }, [periodoMeses]);

  const cargarReportes = async () => {
    try {
      setLoading(true);
      const [ingresos, estados, nuevos, turnos] = await Promise.all([
        axios.get(`/reportes/ingresos-mensuales?meses=${periodoMeses}`),
        axios.get(`/reportes/turnos-por-estado?meses=${periodoMeses}`),
        axios.get(`/reportes/pacientes-nuevos?meses=${periodoMeses}`),
        axios.get(`/reportes/turnos-por-mes?meses=${periodoMeses}`)
      ]);

      setIngresosMensuales(ingresos.data.data || []);
      setTurnosPorEstado(estados.data.data || []);
      setPacientesNuevos(nuevos.data.data || []);
      setTurnosPorMes(turnos.data.data || []);
    } catch (error) {
      console.error('Error al cargar reportes:', error);
      alert('Error al cargar los reportes');
    } finally {
      setLoading(false);
    }
  };

  const formatMes = (mesStr) => {
    if (!mesStr) return '';
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const [year, month] = mesStr.split('-');
    return `${meses[parseInt(month) - 1]} ${year}`;
  };

  const formatMoneda = (valor) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(valor || 0);
  };

  const calcularTotales = () => {
    const totalIngresos = ingresosMensuales.reduce((sum, item) => sum + (item.total || 0), 0);
    const totalTurnos = turnosPorEstado.reduce((sum, item) => sum + (item.cantidad || 0), 0);
    const totalPacientesNuevos = pacientesNuevos.reduce((sum, item) => sum + (item.cantidad || 0), 0);
    
    return { totalIngresos, totalTurnos, totalPacientesNuevos };
  };

  // Exportar a PDF
  const exportarPDF = () => {
    const doc = new jsPDF();
    
    // Título
    doc.setFontSize(18);
    doc.text('📊 Reporte de Consultorio Psicológico', 14, 20);
    
    doc.setFontSize(11);
    doc.text(`Período: Últimos ${periodoMeses} meses`, 14, 28);
    doc.text(`Fecha de generación: ${new Date().toLocaleDateString('es-AR')}`, 14, 34);
    
    // Resumen
    doc.setFontSize(14);
    doc.text('Resumen Ejecutivo', 14, 45);
    
    const resumenData = [
      ['Métrica', 'Valor'],
      ['Ingresos Totales', formatMoneda(totales.totalIngresos)],
      ['Total de Turnos', totales.totalTurnos.toString()],
      ['Pacientes Nuevos', totales.totalPacientesNuevos.toString()]
    ];
    
    doc.autoTable({
      startY: 50,
      head: [resumenData[0]],
      body: resumenData.slice(1),
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] }
    });
    
    // Ingresos Mensuales
    doc.setFontSize(14);
    doc.text('Ingresos Mensuales', 14, doc.lastAutoTable.finalY + 15);
    
    const ingresosData = ingresosMensuales.map(item => [
      formatMes(item.mes),
      formatMoneda(item.total),
      (item.cantidad || 0).toString()
    ]);
    
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Mes', 'Total', 'Cantidad de Pagos']],
      body: ingresosData,
      theme: 'grid',
      headStyles: { fillColor: [16, 185, 129] }
    });
    
    // Turnos por Estado
    doc.addPage();
    doc.setFontSize(14);
    doc.text('Turnos por Estado', 14, 20);
    
    const estadosData = turnosPorEstado.map(item => [
      item.estado.toUpperCase(),
      item.cantidad.toString(),
      `${((item.cantidad / totales.totalTurnos) * 100).toFixed(1)}%`
    ]);
    
    doc.autoTable({
      startY: 25,
      head: [['Estado', 'Cantidad', 'Porcentaje']],
      body: estadosData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] }
    });
    
    // Pacientes Nuevos
    doc.setFontSize(14);
    doc.text('Pacientes Nuevos por Mes', 14, doc.lastAutoTable.finalY + 15);
    
    const pacientesData = pacientesNuevos.map(item => [
      formatMes(item.mes),
      item.cantidad.toString()
    ]);
    
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Mes', 'Cantidad']],
      body: pacientesData,
      theme: 'grid',
      headStyles: { fillColor: [245, 158, 11] }
    });
    
    // Guardar PDF
    doc.save(`reporte-consultorio-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Exportar a Excel
  const exportarExcel = () => {
    // Crear libro de trabajo
    const wb = XLSX.utils.book_new();
    
    // Hoja 1: Resumen
    const resumenWS = XLSX.utils.aoa_to_sheet([
      ['Reporte de Consultorio Psicológico'],
      [`Período: Últimos ${periodoMeses} meses`],
      [`Fecha: ${new Date().toLocaleDateString('es-AR')}`],
      [],
      ['Métrica', 'Valor'],
      ['Ingresos Totales', totales.totalIngresos],
      ['Total de Turnos', totales.totalTurnos],
      ['Pacientes Nuevos', totales.totalPacientesNuevos]
    ]);
    XLSX.utils.book_append_sheet(wb, resumenWS, 'Resumen');
    
    // Hoja 2: Ingresos Mensuales
    const ingresosData = ingresosMensuales.map(item => ({
      'Mes': formatMes(item.mes),
      'Total': item.total,
      'Cantidad de Pagos': item.cantidad || 0
    }));
    const ingresosWS = XLSX.utils.json_to_sheet(ingresosData);
    XLSX.utils.book_append_sheet(wb, ingresosWS, 'Ingresos Mensuales');
    
    // Hoja 3: Turnos por Estado
    const estadosData = turnosPorEstado.map(item => ({
      'Estado': item.estado.toUpperCase(),
      'Cantidad': item.cantidad,
      'Porcentaje': `${((item.cantidad / totales.totalTurnos) * 100).toFixed(1)}%`
    }));
    const estadosWS = XLSX.utils.json_to_sheet(estadosData);
    XLSX.utils.book_append_sheet(wb, estadosWS, 'Turnos por Estado');
    
    // Hoja 4: Pacientes Nuevos
    const pacientesData = pacientesNuevos.map(item => ({
      'Mes': formatMes(item.mes),
      'Cantidad': item.cantidad
    }));
    const pacientesWS = XLSX.utils.json_to_sheet(pacientesData);
    XLSX.utils.book_append_sheet(wb, pacientesWS, 'Pacientes Nuevos');
    
    // Hoja 5: Turnos por Mes Detallado
    const turnosData = turnosPorMes.map(item => ({
      'Mes': formatMes(item.mes),
      'Total': item.total,
      'Completados': item.completados,
      'Cancelados': item.cancelados,
      'No Show': item.no_shows
    }));
    const turnosWS = XLSX.utils.json_to_sheet(turnosData);
    XLSX.utils.book_append_sheet(wb, turnosWS, 'Turnos Detallados');
    
    // Guardar archivo
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, `reporte-consultorio-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const totales = calcularTotales();

  const getColorEstado = (estado) => {
    const colores = {
      'completado': '#10b981',
      'pendiente': '#f59e0b',
      'cancelado': '#ef4444',
      'no_show': '#6b7280'
    };
    return colores[estado] || '#6b7280';
  };

  if (loading) {
    return <div className="reportes-container loading">Cargando reportes...</div>;
  }

  return (
    <div className="reportes-container">
      <div className="reportes-header">
        <h1>📊 Reportes y Análisis</h1>
        <div className="header-actions">
          <div className="periodo-selector">
            <label>Período:</label>
            <select value={periodoMeses} onChange={(e) => setPeriodoMeses(parseInt(e.target.value))}>
              <option value="3">Últimos 3 meses</option>
              <option value="6">Últimos 6 meses</option>
              <option value="12">Último año</option>
            </select>
          </div>
          <div className="export-buttons">
            <button className="btn-export pdf" onClick={exportarPDF} title="Exportar a PDF">
              📄 PDF
            </button>
            <button className="btn-export excel" onClick={exportarExcel} title="Exportar a Excel">
              📊 Excel
            </button>
          </div>
        </div>
      </div>

      {/* Tarjetas de resumen */}
      <div className="resumen-cards">
        <div className="resumen-card ingresos">
          <div className="resumen-icon">💰</div>
          <div className="resumen-content">
            <h3>Ingresos Totales</h3>
            <p className="resumen-valor">{formatMoneda(totales.totalIngresos)}</p>
            <span className="resumen-periodo">{periodoMeses} meses</span>
          </div>
        </div>
        <div className="resumen-card turnos">
          <div className="resumen-icon">📅</div>
          <div className="resumen-content">
            <h3>Total Turnos</h3>
            <p className="resumen-valor">{totales.totalTurnos}</p>
            <span className="resumen-periodo">{periodoMeses} meses</span>
          </div>
        </div>
        <div className="resumen-card pacientes">
          <div className="resumen-icon">👥</div>
          <div className="resumen-content">
            <h3>Pacientes Nuevos</h3>
            <p className="resumen-valor">{totales.totalPacientesNuevos}</p>
            <span className="resumen-periodo">{periodoMeses} meses</span>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="graficos-grid">
        
        {/* Ingresos Mensuales */}
        <div className="grafico-card">
          <h2>💵 Ingresos Mensuales</h2>
          <div className="grafico-contenido">
            {ingresosMensuales.length > 0 ? (
              <div className="grafico-barras">
                {ingresosMensuales.map((item, index) => {
                  const maxValor = Math.max(...ingresosMensuales.map(i => i.total));
                  const altura = (item.total / maxValor) * 100;
                  return (
                    <div key={index} className="barra-wrapper">
                      <div className="barra-container">
                        <div 
                          className="barra"
                          style={{ height: `${altura}%`, backgroundColor: '#10b981' }}
                          title={formatMoneda(item.total)}
                        >
                          <span className="barra-valor">{formatMoneda(item.total)}</span>
                        </div>
                      </div>
                      <div className="barra-label">{formatMes(item.mes)}</div>
                      <div className="barra-cantidad">{item.cantidad} pagos</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="sin-datos">No hay datos de ingresos para mostrar</p>
            )}
          </div>
        </div>

        {/* Turnos por Estado */}
        <div className="grafico-card">
          <h2>📊 Distribución de Turnos</h2>
          <div className="grafico-contenido">
            {turnosPorEstado.length > 0 ? (
              <div className="grafico-dona">
                {turnosPorEstado.map((item, index) => (
                  <div key={index} className="dona-item">
                    <div 
                      className="dona-barra"
                      style={{
                        width: `${(item.cantidad / totales.totalTurnos) * 100}%`,
                        backgroundColor: getColorEstado(item.estado)
                      }}
                    />
                    <div className="dona-info">
                      <span className="dona-label" style={{ color: getColorEstado(item.estado) }}>
                        ● {item.estado.toUpperCase()}
                      </span>
                      <span className="dona-valor">
                        {item.cantidad} ({((item.cantidad / totales.totalTurnos) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="sin-datos">No hay datos de turnos para mostrar</p>
            )}
          </div>
        </div>

        {/* Pacientes Nuevos por Mes */}
        <div className="grafico-card">
          <h2>👥 Crecimiento de Pacientes</h2>
          <div className="grafico-contenido">
            {pacientesNuevos.length > 0 ? (
              <div className="grafico-linea">
                <div className="linea-grid">
                  {pacientesNuevos.map((item, index) => {
                    const maxValor = Math.max(...pacientesNuevos.map(i => i.cantidad));
                    const altura = (item.cantidad / maxValor) * 100;
                    return (
                      <div key={index} className="linea-punto-wrapper">
                        <div className="linea-punto-container">
                          <div 
                            className="linea-punto"
                            style={{ bottom: `${altura}%` }}
                            title={`${item.cantidad} pacientes`}
                          >
                            <span className="punto-valor">{item.cantidad}</span>
                            <div className="punto-circulo"></div>
                          </div>
                        </div>
                        <div className="linea-label">{formatMes(item.mes)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="sin-datos">No hay datos de pacientes nuevos para mostrar</p>
            )}
          </div>
        </div>

        {/* Turnos por Mes (Desglosados) */}
        <div className="grafico-card full-width">
          <h2>📈 Evolución de Turnos por Estado</h2>
          <div className="grafico-contenido">
            {turnosPorMes.length > 0 ? (
              <div className="grafico-apilado">
                <div className="apilado-leyenda">
                  <span className="leyenda-item completado">● Completados</span>
                  <span className="leyenda-item cancelado">● Cancelados</span>
                  <span className="leyenda-item no-show">● No Show</span>
                </div>
                <div className="apilado-barras">
                  {turnosPorMes.map((item, index) => {
                    const totalMes = item.total || 1;
                    const pctCompletados = (item.completados / totalMes) * 100;
                    const pctCancelados = (item.cancelados / totalMes) * 100;
                    const pctNoShows = (item.no_shows / totalMes) * 100;
                    
                    return (
                      <div key={index} className="apilado-columna">
                        <div className="apilado-barra-container">
                          <div 
                            className="apilado-segmento completado"
                            style={{ height: `${pctCompletados}%` }}
                            title={`${item.completados} completados`}
                          >
                            {item.completados > 0 && <span>{item.completados}</span>}
                          </div>
                          <div 
                            className="apilado-segmento cancelado"
                            style={{ height: `${pctCancelados}%` }}
                            title={`${item.cancelados} cancelados`}
                          >
                            {item.cancelados > 0 && <span>{item.cancelados}</span>}
                          </div>
                          <div 
                            className="apilado-segmento no-show"
                            style={{ height: `${pctNoShows}%` }}
                            title={`${item.no_shows} no show`}
                          >
                            {item.no_shows > 0 && <span>{item.no_shows}</span>}
                          </div>
                        </div>
                        <div className="apilado-label">{formatMes(item.mes)}</div>
                        <div className="apilado-total">Total: {item.total}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="sin-datos">No hay datos de turnos por mes para mostrar</p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Reportes;
